import { create } from "zustand";

import type { AuthUser } from "@/Utils/Auth/authSession";
import {
  clearStoredUser,
  fetchCurrentUser,
  readStoredUser,
  writeStoredUser,
} from "@/Utils/Auth/authSession";
import { loggerService, LogTag } from "@/Utils/LoggerService";

export type User = AuthUser;

interface AuthState {
  user: User | null;

  isAuthenticated: boolean;

  /** Sets the authenticated user and persists it (non-sensitive) for reload. */
  setUser: (user: User) => void;

  /** Clears the user. Tokens are cleared server-side via /auth/logout. */
  clearUser: () => void;

  /**
   * Revalidates the session against the httpOnly cookie via /auth/me and syncs
   * the store. Call once on app boot.
   */
  hydrate: () => Promise<void>;
}

const storedUser = readStoredUser();

export const useAuthStore = create<AuthState>((set) => ({
  user: storedUser,

  isAuthenticated: Boolean(storedUser),

  setUser: (user) => {
    loggerService.info(LogTag.AUTH, "User authenticated", user.email);

    writeStoredUser(user);

    set({ user, isAuthenticated: true });
  },

  clearUser: () => {
    loggerService.info(LogTag.AUTH, "User logged out");

    clearStoredUser();

    set({ user: null, isAuthenticated: false });
  },

  hydrate: async () => {
    const user = await fetchCurrentUser();

    if (user) {
      writeStoredUser(user);
      set({ user, isAuthenticated: true });
    } else {
      clearStoredUser();
      set({ user: null, isAuthenticated: false });
    }
  },
}));
